import { Attr, BelongsTo, HasMany, Model } from "spraypaint";

import { type OrganisationReportSummaryType } from "shared/modules/OrganisationReport/types";
import { ApplicationRecord } from "shared/services/spraypaint";

import type ActiveStorageAttachment from "./ActiveStorageAttachment";
import type Organisation from "./Organisation";
import type OrganisationBrand from "./OrganisationBrand";
import type OrganisationReportSpecDateRange from "./OrganisationReportSpecDateRange";
import type OrganisationReportSpecGroup from "./OrganisationReportSpecGroup";
import type OrganisationReportSpecProduct from "./OrganisationReportSpecProduct";
import type OrganisationReportSpecsStreams from "./OrganisationReportSpecsStreams";
import type User from "./User";

@Model()
class OrganisationReportSpec extends ApplicationRecord {
  static jsonapiType = "organisation_report_specs";

  @HasMany() attachments: ActiveStorageAttachment[];

  @Attr() autoDraftEnabled: boolean;

  @Attr() autoDraftRules: any[];

  @Attr() contactEmails: any[];

  @HasMany()
  contentElements: OrganisationReportSpecsStreams[];

  @Attr({ persist: false }) contentRange: string;

  @Attr({ persist: false }) createdAt: string;

  @Attr() customSender: string;

  @HasMany() dateRanges: OrganisationReportSpecDateRange[];

  @Attr() deliverAt: number;

  @Attr() deliveryTime: string;

  @Attr() disclaimer: string;

  @Attr() distributionListId: number;

  @Attr() emptyFolderMessage: string;

  @Attr() exclusionsNotes: string;

  @Attr() folderIds: number[];

  @Attr() fontSize: string;

  @Attr() footer: string;

  @Attr() formatNotes: string;

  @Attr() fridayRange: number;

  @BelongsTo() group: OrganisationReportSpecGroup;

  @Attr() groupId: number;

  @Attr() hideReport: boolean;

  @Attr() iconUrl: string;

  @Attr() includeCompletePdf: boolean;

  @Attr() includeCsv: boolean;

  @Attr() includeDisclaimer: boolean;

  @Attr() includeFolderPrintClipsPdf: boolean;

  @Attr() includePdf: boolean;

  @Attr() includePrintClipsPdf: boolean;

  @Attr() includePrintClipsPdfWithAfrClippings: boolean;

  @Attr() inclusionsNotes: string;

  @Attr() mentionsDisplay: string;

  @Attr() message: string;

  @Attr() mondayRange: number;

  @Attr() onFriday: boolean;

  @Attr() onMonday: boolean;

  @Attr() onSaturday: boolean;

  @Attr() onSunday: boolean;

  @Attr() onThursday: boolean;

  @Attr() onTuesday: boolean;

  @Attr() onWednesday: boolean;

  @Attr() order: string;

  @BelongsTo() organisation: Organisation;

  @BelongsTo() organisationBrand: OrganisationBrand;

  @Attr() organisationBrandId: number;

  @Attr() organisationId: number;

  @HasMany()
  organisationReportSpecProducts: OrganisationReportSpecProduct[];

  // Preferred stream relationship. Use `contentElements` in new code;
  // `organisationReportSpecsStreams` is a legacy association kept for backwards compatibility.
  @HasMany()
  organisationReportSpecsStreams: OrganisationReportSpecsStreams[];

  @Attr() overviewNotes: string;

  @Attr() pdfReportTemplateName: string;

  @Attr() position: number;

  @Attr() prefillEmptyFolders: boolean;

  @Attr() recipients: any[];

  @Attr() requiresProofing: boolean;

  @Attr() saturdayRange: number;

  @Attr() scheduleOnPublicHoliday: boolean;

  @Attr() sendAsOrganisation: boolean;

  @Attr() showAve: boolean;

  @Attr() showDomainAuthority: boolean;

  @Attr() showExcerpts: boolean;

  @Attr() showImpactScore: boolean;

  @Attr() showKeywordCount: boolean;

  @Attr() showPotentialAudienceReach: boolean;

  @Attr() showRestrictedContent: boolean;

  @Attr() showRssLink: boolean;

  @Attr() showSentiment: boolean;

  @Attr() showSummary: boolean;

  @Attr() showSyndications: boolean;

  @Attr() sourcesNotes: string;

  @Attr() status: string;

  @Attr() subject: string;

  @Attr() summaryLinkBriefIds: number[];

  @Attr() summaryType: OrganisationReportSummaryType;

  @Attr() sundayRange: number;

  @Attr() task: string;

  @Attr() thursdayRange: number;

  @Attr() timeZone: string;

  @Attr() title: string;

  @Attr() tuesdayRange: number;

  @Attr({ persist: false }) updatedAt: string;

  @Attr() useDistributionList: boolean;

  @BelongsTo() userAccount: User;

  @Attr() userAccountId: number;

  @Attr() viewerNotes: string;

  @Attr() wednesdayRange: number;

  @Attr() weekdayAutoDraftAt: number;

  @Attr() weekendAutoDraftAt: number;

  @Attr() weekendDeliverAt: number;
}

export default OrganisationReportSpec;
